/**
 * Outbound Logistics (Sales Control) References — thin wrapper over the shared
 * `@/components/references` shell, driven by the canonical KHO (catalog).
 * Owns state (pageKey/search), the RTK query, and server-prefs column + width
 * controllers.
 */
import { useMemo, useState } from 'react'

import {
  OUTBOUND_REFERENCE_ORDER,
  REFERENCE_CATALOG,
  type ReferencePageKey,
} from '@/components/references/catalog'
import ReferencesView from '@/components/references/ReferencesView'
import type { ReferenceMenuColumn } from '@/components/references/types'
import { useColumnWidths } from '@/hooks/useColumnWidths'
import { useGetSaleControlReferenceRowsQuery } from '@/store/api/saleControlApi'
import { useLocalDatasheetColumns } from '@/utils/datasheetColumns/useLocalDatasheetColumns'

export interface SaleControlReferencesViewProps {
  /** Controlled selected page (lifted lên SaleControlPage để header enable/disable filter). */
  pageKey?: ReferencePageKey
  onPageKeyChange?: (key: ReferencePageKey) => void
  startDate?: string
  endDate?: string
  isFinalized?: string
}

export default function SaleControlReferencesView({
  pageKey: controlledPageKey,
  onPageKeyChange,
  startDate,
  endDate,
  isFinalized,
}: SaleControlReferencesViewProps = {}) {
  const [internalPageKey, setInternalPageKey] = useState<ReferencePageKey>('service-invoices')
  const pageKey = controlledPageKey ?? internalPageKey
  const setPageKey = (key: ReferencePageKey) => {
    setInternalPageKey(key)
    onPageKeyChange?.(key)
  }
  const [searchByPage, setSearchByPage] = useState<Partial<Record<ReferencePageKey, string>>>({})

  const page = REFERENCE_CATALOG[pageKey]
  const search = searchByPage[pageKey] ?? ''

  // Filter mỗi page tự khai trong catalog — chỉ forward param page cần.
  const pageFilters = page.filters ?? []
  const useDate = pageFilters.includes('date')
  const useFinalized = pageFilters.includes('finalized')
  const queryArg = useMemo(
    () => ({
      pageKey,
      limit: page.limit ?? 5000,
      ...(useDate ? { startDate, endDate } : {}),
      ...(useFinalized ? { isFinalized } : {}),
    }),
    [page.limit, pageKey, useDate, useFinalized, startDate, endDate, isFinalized],
  )

  const { currentData, isFetching, error, refetch } = useGetSaleControlReferenceRowsQuery(queryArg, {
    refetchOnMountOrArgChange: true,
  })

  const menuColumns = useMemo<ReferenceMenuColumn[]>(
    () => page.columns.map((c) => ({ ...c, key: c.field, label: c.headerName })),
    [page.columns],
  )
  // Column layout keyed theo canonical page-key (dùng chung xuyên function):
  // cùng 1 page mở ở function nào cũng chung 1 layout width + ẩn/hiện, per-user.
  const columnsCtrl = useLocalDatasheetColumns({
    allColumns: menuColumns,
    storageKey: `references.${pageKey}`,
  })
  const widths = useColumnWidths(`references.${pageKey}`)

  const rows = currentData?.items ?? []
  return (
    <ReferencesView<ReferencePageKey>
      title="Outbound Logistics - References"
      subtitle="Access switchboard parity for the 12 reference pages."
      pageOrder={OUTBOUND_REFERENCE_ORDER}
      configs={REFERENCE_CATALOG}
      pageKey={pageKey}
      onPageKeyChange={setPageKey}
      rows={rows}
      search={search}
      onSearchChange={(value) => setSearchByPage((prev) => ({ ...prev, [pageKey]: value }))}
      loading={isFetching && rows.length === 0}
      fetching={isFetching && rows.length > 0}
      error={error}
      onRetry={() => {
        void refetch()
      }}
      columnsCtrl={columnsCtrl}
      widths={widths}
      totalsMode="all-numeric-except-noTotal"
      fallbackToConfigWidth
    />
  )
}
